import type { BambooStyle } from "./types";

function drawLeaf(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  angle: number,
  length: number,
  color: string,
): void {
  context.save();
  context.translate(x, y);
  context.rotate(angle);
  context.fillStyle = color;
  context.beginPath();
  context.moveTo(0, 0);
  context.quadraticCurveTo(length * 0.45, -length * 0.22, length, 0);
  context.quadraticCurveTo(length * 0.45, length * 0.2, 0, 0);
  context.fill();
  context.restore();
}

export function createShareCard(
  character: string,
  phrase: string,
  reflection: string,
  style: BambooStyle,
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1620;
  const context = canvas.getContext("2d");
  if (!context) return Promise.reject(new Error("当前浏览器不支持图片合成。"));

  const paper = context.createLinearGradient(0, 0, 1080, 1620);
  paper.addColorStop(0, "#f7f1e5");
  paper.addColorStop(0.55, "#efe5d3");
  paper.addColorStop(1, "#e6dbc8");
  context.fillStyle = paper;
  context.fillRect(0, 0, 1080, 1620);

  context.globalAlpha = 0.08;
  for (let i = 0; i < 420; i += 1) {
    context.fillStyle = i % 3 === 0 ? "#171716" : "#a68f70";
    context.fillRect((i * 83) % 1080, (i * 137) % 1620, 1, 1);
  }
  context.globalAlpha = 1;

  context.strokeStyle = "#242321";
  context.lineWidth = 15;
  context.beginPath();
  context.moveTo(315, 1320);
  context.bezierCurveTo(300, 1000, 360, 650, style === "wind" ? 270 : 340, 230);
  context.stroke();
  context.lineWidth = 9;
  context.beginPath();
  context.moveTo(335, 870);
  context.quadraticCurveTo(520, 760, 680, 520);
  context.moveTo(330, 1050);
  context.quadraticCurveTo(540, 980, 760, 820);
  context.stroke();

  const density = style === "dense" ? 50 : style === "sparse" ? 26 : 38;
  for (let i = 0; i < density; i += 1) {
    const branch = i % 2;
    const x = 360 + ((i * 79) % 390) + branch * 20;
    const y = 390 + ((i * 113) % 670);
    const wind = style === "wind" ? -0.48 : style === "rain" ? 0.95 : i % 2 ? 0.4 : -0.5;
    drawLeaf(context, x, y, wind + (i % 5) * 0.08, 76 + (i % 4) * 10, i % 4 ? "#242321" : "#0f0f0e");
  }

  context.fillStyle = "rgba(242,235,221,0.72)";
  context.fillRect(110, 210, 330, 380);
  context.fillStyle = "#171716";
  context.font = "700 270px 'Noto Serif SC','Songti SC',serif";
  context.textAlign = "center";
  context.fillText(character, 275, 510);

  context.fillStyle = "#a23b2a";
  context.fillRect(820, 126, 118, 118);
  context.fillStyle = "#f2ebdd";
  context.font = "700 42px serif";
  context.fillText("藏心", 879, 200);

  context.textAlign = "left";
  context.fillStyle = "#171716";
  context.font = "700 76px 'Noto Serif SC','Songti SC',serif";
  context.fillText(phrase, 110, 1320);
  context.fillStyle = "#5d5a54";
  context.font = "36px 'Noto Serif SC','Songti SC',serif";
  const reflectionLine = reflection.length > 20 ? `${reflection.slice(0, 20)}…` : reflection;
  context.fillText(reflectionLine, 112, 1390);
  context.fillStyle = "#242321";
  context.font = "30px sans-serif";
  context.fillText("风雨藏心 · 一叶藏一言", 112, 1510);

  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("分享图生成失败。"))), "image/png", 0.94),
  );
}

function drawLeafCharacter(character: string, width = 300, height = 330): HTMLCanvasElement {
  const layer = document.createElement("canvas");
  layer.width = width;
  layer.height = height;
  const context = layer.getContext("2d");
  if (!context) return layer;
  for (let row = 0; row < 15; row += 1) {
    for (let column = 0; column < 13; column += 1) {
      const x = -10 + column * (width / 12);
      const y = 5 + row * (height / 14.2);
      drawLeaf(context, x, y, (row + column) % 2 ? -0.38 : 0.44, width / 9.2, column % 3 ? "#242321" : "#0f0f0e");
    }
  }
  context.globalCompositeOperation = "destination-in";
  context.fillStyle = "#171716";
  context.font = `900 ${Math.round(width * 0.78)}px 'Noto Serif SC','Songti SC',serif`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(character, width / 2, height / 2 + 12);
  context.globalCompositeOperation = "source-over";
  context.strokeStyle = "rgba(23,23,22,.36)";
  context.lineWidth = 3.2;
  context.strokeText(character, width / 2, height / 2 + 12);
  return layer;
}

export function createNameShareCard(name: string, style: BambooStyle): Promise<Blob> {
  const characters = Array.from(name).slice(0, 4);
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1620;
  const context = canvas.getContext("2d");
  if (!context) return Promise.reject(new Error("当前浏览器不支持图片合成。"));

  const paper = context.createLinearGradient(0, 0, 1080, 1620);
  paper.addColorStop(0, "#f7f1e5");
  paper.addColorStop(1, "#e8ddca");
  context.fillStyle = paper;
  context.fillRect(0, 0, 1080, 1620);

  context.strokeStyle = "rgba(36,35,33,.72)";
  context.lineWidth = 13;
  context.beginPath();
  context.moveTo(160, 1440);
  context.bezierCurveTo(120, 1020, 250, 580, style === "wind" ? 150 : 260, 120);
  context.moveTo(900, 1480);
  context.bezierCurveTo(950, 1060, 820, 620, style === "rain" ? 890 : 760, 180);
  context.stroke();

  const positions =
    characters.length === 1
      ? [[390, 410]]
      : characters.length === 2
        ? [[210, 420], [570, 420]]
        : characters.length === 3
          ? [[210, 290], [570, 290], [390, 680]]
          : [[210, 290], [570, 290], [210, 670], [570, 670]];
  characters.forEach((character, index) => {
    const layer = drawLeafCharacter(character);
    const [x, y] = positions[index];
    context.drawImage(layer, x, y);
  });

  context.fillStyle = "#a23b2a";
  context.fillRect(820, 110, 116, 116);
  context.fillStyle = "#f2ebdd";
  context.font = "700 40px serif";
  context.textAlign = "center";
  context.fillText("藏名", 878, 180);
  context.fillStyle = "#171716";
  context.font = "700 64px 'Noto Serif SC','Songti SC',serif";
  context.fillText(name, 540, 1375);
  context.fillStyle = "#5d5a54";
  context.font = "32px sans-serif";
  context.fillText("风雨藏心 · 一叶藏一名", 540, 1460);

  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("藏名图生成失败。"))), "image/png", 0.94),
  );
}
